import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { AllEntities } from "../../entities/utils";
import ViewLeadsDrawer from "./ViewLeadsDrawer";

type DrawerData = {
  savedLeadIds: string[];
  entityType: AllEntities;
};

type ViewLeadsDrawerContextValue = {
  openViewLeadsDrawer: (data: NonNullable<DrawerData>) => void;
  closeViewLeadsDrawer: () => void;
};

const ViewLeadsDrawerContext = createContext<ViewLeadsDrawerContextValue | null>(null);

export function useViewLeadsDrawer() {
  const ctx = useContext(ViewLeadsDrawerContext);
  if (!ctx) throw new Error("useViewLeadsDrawer must be used within ViewLeadsDrawerProvider");
  return ctx;
}

/**
 * Provides a single, shared ViewLeadsDrawer instance for all consumers.
 *
 * This avoids rendering one drawer per table row by centralising drawer state
 * and dynamically supplying the active entity data when a row requests it.
 * Improves performance and ensures only one drawer can be open at a time.
 */
export function ViewLeadsDrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<DrawerData | null>(null);

  const openViewLeadsDrawer = useCallback((next: DrawerData) => {
    setData(next);
    setIsOpen(true);
  }, []);

  const closeViewLeadsDrawer = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  const value = useMemo(
    () => ({ openViewLeadsDrawer, closeViewLeadsDrawer }),
    [openViewLeadsDrawer, closeViewLeadsDrawer],
  );

  return (
    <ViewLeadsDrawerContext.Provider value={value}>
      {children}

      {/* Single shared drawer instance */}
      {data && (
        <ViewLeadsDrawer
          savedLeadIds={data.savedLeadIds}
          entityType={data.entityType}
          isOpen={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) setData(null);
          }}
        />
      )}
    </ViewLeadsDrawerContext.Provider>
  );
}
